import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useUser, useOrganization, useOrganizationList, useClerk, useAuth } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { Sidebar } from './Sidebar';
import { ThemeSelector } from './ThemeSelector';
import { disposeOrgConnection } from '../wsConnection';
import { useStore } from '../store';
import { usePlatformAdmin } from '../hooks';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

interface AdminOrg {
  id: string;
  name: string;
  deactivatedAt?: string | null;
}

function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const clerk = useClerk();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { isPlatformAdmin } = usePlatformAdmin();
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [focusedOrgIndex, setFocusedOrgIndex] = useState(-1);
  const [orgSearch, setOrgSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const adminOrgOverride = useStore(s => s.adminOrgOverride);
  const storeOrgName = useStore(s => s.organization?.name);

  // Admin org list (all platform orgs, fetched lazily)
  const [adminOrgs, setAdminOrgs] = useState<AdminOrg[]>([]);
  const [adminOrgsLoaded, setAdminOrgsLoaded] = useState(false);

  // Fetch all orgs when admin opens dropdown
  useEffect(() => {
    if (!isPlatformAdmin || !showOrgDropdown || adminOrgsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken({ template: 'default' });
        if (!token || cancelled) return;
        const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json() as { organizations: AdminOrg[] };
        if (!cancelled) {
          setAdminOrgs(data.organizations ?? []);
          setAdminOrgsLoaded(true);
        }
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [isPlatformAdmin, showOrgDropdown, adminOrgsLoaded, getToken]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (showOrgDropdown && isPlatformAdmin) {
      // Small delay to allow the dropdown to render
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [showOrgDropdown, isPlatformAdmin]);

  // Build combined org list for admins
  const memberOrgIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of userMemberships?.data ?? []) {
      ids.add(m.organization.id);
    }
    return ids;
  }, [userMemberships?.data]);

  const combinedOrgList = useMemo(() => {
    type OrgItem = { id: string; name: string; isMember: boolean };
    const members: OrgItem[] = (userMemberships?.data ?? []).map(m => ({
      id: m.organization.id,
      name: m.organization.name,
      isMember: true,
    }));

    if (!isPlatformAdmin) return members;

    const adminOnly: OrgItem[] = adminOrgs
      .filter(o => !memberOrgIds.has(o.id) && !o.deactivatedAt)
      .map(o => ({ id: o.id, name: o.name, isMember: false }));

    // Sort each group alphabetically
    members.sort((a, b) => a.name.localeCompare(b.name));
    adminOnly.sort((a, b) => a.name.localeCompare(b.name));

    return [...members, ...adminOnly];
  }, [userMemberships?.data, isPlatformAdmin, adminOrgs, memberOrgIds]);

  const filteredOrgList = useMemo(() => {
    if (!orgSearch) return combinedOrgList;
    const q = orgSearch.toLowerCase();
    return combinedOrgList.filter(o => o.name.toLowerCase().includes(q));
  }, [combinedOrgList, orgSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false);
        setFocusedOrgIndex(-1);
        setOrgSearch('');
      }
    };

    if (showOrgDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOrgDropdown]);

  // Keyboard navigation for org dropdown
  useEffect(() => {
    if (!showOrgDropdown) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setShowOrgDropdown(false);
          setFocusedOrgIndex(-1);
          setOrgSearch('');
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedOrgIndex(prev => Math.min(prev + 1, filteredOrgList.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedOrgIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedOrgIndex >= 0 && focusedOrgIndex < filteredOrgList.length) {
            const item = filteredOrgList[focusedOrgIndex];
            handleOrgSelect(item.id, item.isMember);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showOrgDropdown, focusedOrgIndex, filteredOrgList]);

  const handleLogout = async () => {
    await clerk.signOut();
  };

  const handleExitAdminView = () => {
    disposeOrgConnection();
    useStore.getState().reset();
    useStore.getState().setAdminOrgOverride(null);
    navigate({ to: '/' });
  };

  const handleOrgSelect = useCallback(async (orgId: string, isMember: boolean) => {
    setShowOrgDropdown(false);
    setOrgSearch('');

    if (isMember) {
      if (!setActive) return;
      // Clear any admin override first
      if (adminOrgOverride) {
        useStore.getState().setAdminOrgOverride(null);
      }
      // Tear down WS singleton and clear all state
      disposeOrgConnection();
      useStore.getState().reset();
      localStorage.clear();
      sessionStorage.clear();

      // Switch Clerk org context
      await setActive({ organization: orgId });

      // Navigate to root — __root.tsx effect detects new organization?.id
      // and calls initOrgConnection() to reconnect
      navigate({ to: '/' });
    } else {
      // Admin impersonation via adminOrgOverride
      disposeOrgConnection();
      useStore.getState().reset();
      useStore.getState().setAdminOrgOverride(orgId);
      navigate({ to: '/' });
    }
  }, [setActive, adminOrgOverride, navigate]);

  // Determine display name for the org button
  const displayOrgName = adminOrgOverride
    ? (storeOrgName || 'Admin View')
    : (organization?.name || 'Select Organization');

  // Determine the "active" org ID for highlighting
  const activeOrgId = adminOrgOverride || organization?.id;

  // Partition filtered list into members and admin-only for section rendering
  const memberOrgs = useMemo(() => filteredOrgList.filter(o => o.isMember), [filteredOrgList]);
  const adminOnlyOrgs = useMemo(() => filteredOrgList.filter(o => !o.isMember), [filteredOrgList]);

  return (
    <div className={styles.container}>
      <Sidebar
        onLogout={handleLogout}
      />

      <main className={styles.main}>
        <div className={styles.topBar}>
          {/* Organization Selector */}
          <div className={styles.orgSelector} ref={dropdownRef}>
            <button
              className={`${styles.orgButton} ${adminOrgOverride ? styles.orgButtonAdmin : ''}`}
              onClick={() => {
                setShowOrgDropdown(!showOrgDropdown);
                setFocusedOrgIndex(-1);
                setOrgSearch('');
              }}
              aria-expanded={showOrgDropdown}
              aria-haspopup="listbox"
            >
              <span>{displayOrgName}</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 5L6 8L9 5" strokeLinecap="square" />
              </svg>
            </button>

            {showOrgDropdown && (
              <div className={styles.orgDropdown} role="listbox">
                {isPlatformAdmin && (
                  <div className={styles.orgSearchWrapper}>
                    <input
                      ref={searchInputRef}
                      className={styles.orgSearchInput}
                      type="text"
                      placeholder="Search organizations..."
                      value={orgSearch}
                      onChange={e => {
                        setOrgSearch(e.target.value);
                        setFocusedOrgIndex(-1);
                      }}
                    />
                  </div>
                )}

                {memberOrgs.length > 0 && (
                  <>
                    {isPlatformAdmin && adminOnlyOrgs.length > 0 && (
                      <div className={styles.orgSectionLabel}>Your Organizations</div>
                    )}
                    {memberOrgs.map((org) => {
                      const globalIndex = filteredOrgList.indexOf(org);
                      return (
                        <div
                          key={org.id}
                          role="option"
                          aria-selected={org.id === activeOrgId}
                          className={`${styles.orgOption} ${org.id === activeOrgId ? styles.active : ''} ${globalIndex === focusedOrgIndex ? styles.focused : ''}`}
                          onClick={() => handleOrgSelect(org.id, true)}
                        >
                          {org.name}
                          {org.id === activeOrgId && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 7L6 10L11 4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {isPlatformAdmin && adminOnlyOrgs.length > 0 && (
                  <>
                    <div className={styles.orgSectionLabel}>All Organizations</div>
                    {adminOnlyOrgs.map((org) => {
                      const globalIndex = filteredOrgList.indexOf(org);
                      return (
                        <div
                          key={org.id}
                          role="option"
                          aria-selected={org.id === activeOrgId}
                          className={`${styles.orgOption} ${styles.orgOptionAdmin} ${org.id === activeOrgId ? styles.active : ''} ${globalIndex === focusedOrgIndex ? styles.focused : ''}`}
                          onClick={() => handleOrgSelect(org.id, false)}
                        >
                          <span className={styles.orgAdminName}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2">
                              <path d="M5 1L1 3.5V6.5L5 9L9 6.5V3.5L5 1Z" />
                            </svg>
                            {org.name}
                          </span>
                          {org.id === activeOrgId && (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 7L6 10L11 4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {filteredOrgList.length === 0 && (
                  <div className={styles.orgOption} style={{ color: 'var(--text-secondary)', cursor: 'default' }}>
                    No organizations found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Info & Theme */}
          <div className={styles.userInfo}>
            <span>{user?.primaryEmailAddress?.emailAddress}</span>
            <ThemeSelector />
          </div>
        </div>

        {adminOrgOverride && (
          <div className={styles.adminBanner}>
            <div className={styles.adminBannerText}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M7 1L1 4.5V9.5L7 13L13 9.5V4.5L7 1Z" />
              </svg>
              Viewing: {storeOrgName || adminOrgOverride}
            </div>
            <button className={styles.adminExitButton} onClick={handleExitAdminView}>
              Exit Admin View
            </button>
          </div>
        )}

        <div className={styles.content}>
          {children}
        </div>
      </main>
    </div>
  );
};
